import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Popup } from "@/components/common/Popup";
import { SpotItem } from "@/components/spot/SpotItem";
import { SearchForm } from "@/components/common/SearchForm";
import { Dropdown } from "@/components/ui/filterSelect";
import { GridIcon } from "@/components/custom/GridIcon";
import { SpotInfo } from "@/components/spot/SpotInfo";
import { Trash2, Plus, ArrowDownNarrowWide, ArrowUpWideNarrow, Loader2 } from "lucide-react";

import { SpotInfoDisplaySchema } from "@/components/spot/SpotSchema";
import { sortData } from "@/helper/sort";
import { SortCategory } from "@/helper/sort";
import { useApiFetch } from "@/helper/apiFetch";
import { useAuth } from "@/auth";
import { useTranslate } from "@/helper/translate";
import React from "react";





// Utility: Filter search input
function applySearchFilter(data: SpotInfoDisplaySchema[], query: string): SpotInfoDisplaySchema[] {
  const term = query.trim().toLowerCase();
  if (term == '') return data;

  return data.filter(item =>
    // item.Customer.toLowerCase().includes(term) ||
    item.name.toLowerCase().includes(term)
  );
}
type Lookup = { id: string; name: string, allowTemplate?: boolean };




// Component
export function SpotPage() {
  const apiFetch = useApiFetch();
    const { translate } = useTranslate();
  
  const { linkedCustomerId } = useAuth();
  const [params] = useSearchParams();
  const spotId = params.get("spotId"); // string | null
  const selectedCustomerId = params.get("customerId"); // string | null

  const colHeaders = [{ name: "Select", sort: "" }, { name: "Name", sort: "name" }, { name: "Type", sort: "mimeType" }, { name: "Duration", sort: "length" }, { name: "Status", sort: "statusId" }, { name: "DateChanged", sort: "dateChanged" }];

  const [isLoaded, setIsLoaded] = useState(false);

  const [customers, setCustomers] = useState<Lookup[]>([]);
  const [spots, setSpots] = useState<SpotInfoDisplaySchema[]>([]);
  const [statusList, setStatusList] = useState<Lookup[]>([]);
  const [listMode, setListMode] = useState(true);
  const [statusId, setStatus] = useState(0);
  const [sortCategory, setSortCategory] = useState<SortCategory>();
  const [searchText, setSearchText] = useState<string>("");
  const [editSpotId, setEditSpot] = useState<string | null>(spotId);
  const [selectedSpots, setSelectedSpots] = useState<string[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>(selectedCustomerId || "");
  const [isDeleting, setIsDeleting] = useState(false);


  const handleSortCategoryChange = (newCategory: string | number) => {
    const newC: SortCategory = {
      name: newCategory,
      direction: sortCategory?.name === newCategory ? !sortCategory?.direction : true
    }

    setSortCategory(newC);
    setSpots(sortData(spots, newC));
  };

  const handleCheckChange = (id: string, checked: boolean) => {
    setSelectedSpots(prev =>
      checked ? [...prev, id] : prev.filter(spotId => spotId !== id)
    );
  };

  const deleteSpots = async () => {
    if (isDeleting || selectedSpots.length === 0) return;
    setIsDeleting(true);
    try {

   const ids = [...selectedSpots];

  for (const spotId of ids) {
    try {
      const res = await apiFetch(`/App/DeleteSpot?spotId=${encodeURIComponent(spotId)}`, {
        method: "GET",
      });

      if (res.ok) {
        setSpots(prev => prev.filter(s => s.id !== spotId));
        setSelectedSpots(prev => prev.filter(s => s !== spotId));
      } else {
        const text = await res.text();
        setSpots(prev => prev.map(s => s.id === spotId ? { ...s, error: text } : s));
      }
    } catch (err) {
      console.error(err);
      setSpots(prev => prev.map(s => s.id === spotId ? { ...s, error: "Network/timeout error" } : s));
    }
  }
    } finally {
      setIsDeleting(false);
    }



  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const input = form.querySelector("input");
    setSearchText(input?.value.toLowerCase() || "");
  };

  const filteredData = applySearchFilter(spots, searchText);
  const spotItems = filteredData.map((item, index) => (
    item.isDeleted || statusId > 0 && item.statusId != statusId ? null : (
      <SpotItem
        key={item.id}
        item={item}
        listMode={listMode}
        isSelected={selectedSpots.includes(item.id)}
        onClick={() => setEditSpot(item.id)}
        onCheckChange={handleCheckChange}
      />
    )
  ));



  const updateSpot = (spot: SpotInfoDisplaySchema | null) => {
    if (spot) {
      if (editSpotId == "")
        setSpots(prev => [...prev, spot]);
      else
        setSpots(prev =>
          prev.map(c => (c.id === spot.id ? spot : c))
        );
    }
  };

  useEffect(() => {
    if (linkedCustomerId != "Admin") {
      setCustomers([
        { id: linkedCustomerId, name: "" }
      ]);
      setSelectedCustomer(linkedCustomerId);

    }
    else {
      apiFetch(`/App/GetCustomerLookup?all=true`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch customers");
          return res.json();
        })
        .then((data) => {
          setCustomers(data);
          if(selectedCustomerId  )
            setSelectedCustomer(selectedCustomerId);
          else  if (data.length > 0 ) {
           // setSelectedCustomer(data[0].id);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [linkedCustomerId]);

  useEffect(() => {
    if (selectedCustomer) {
      setIsLoaded(false);

      apiFetch(`/App/GetSpots?customerId=${selectedCustomer}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch spots");
          return res.json();
        })
        .then((spotData) => {
          
          setSpots(spotData);
          var list: Lookup[] = [{ id: "0", name: translate("dd.All")  }];
          spotData.forEach((spot: SpotInfoDisplaySchema) => {
            if (list.findIndex(s => s.id == spot.statusId.toString()) < 0)
              list.push({ id: spot.statusId.toString(), name: translate("ui.status_" + spot.statusId) });

          });
          setStatusList(list);
          setIsLoaded(true);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [selectedCustomer]);

  return (
    <div className="h-[calc(100vh-60px)] flex flex-col">
      <div className="content-header flex justify-between pt-[0.6rem] pb-[0.6rem] px-8 bg-gray-100 dark:bg-zinc-900">
        <div className="flex items-center gap-4 min-w-48">
          <h1 className="font-bold text-2xl">{translate("ui.spots")}</h1>
          {selectedCustomer != "" && (
          <Plus className="lucide lucide-plus w-7 h-7 cursor-pointer bg-sky-600 hover:bg-sky-700 dark:bg-orange-400 dark:hover:bg-orange-500 rounded-full text-white dark:text-black" onClick={() => setEditSpot("")} />
          )}
          {(selectedSpots.length > 0 || isDeleting) && (
            isDeleting ? (
              <Loader2 className="w-7 h-7 animate-spin bg-rose-600 rounded-full text-white p-[3px]" />
            ) : (
              <Trash2 className="w-7 h-7 cursor-pointer bg-rose-600 hover:bg-rose-700 rounded-full text-white p-[3px]" onClick={deleteSpots} />
            )
          )}
        </div>
        {linkedCustomerId == "Admin" && (
          <Dropdown className="mx-8 min-w-[24rem]"
            source={customers}
            value={selectedCustomer}
            getLabel={(x) => x.name}
            getValue={(x) => x.id}
            searchable={true}
            onChange={(value) => setSelectedCustomer(value as string)}
          />
        )}
   {statusList.length > 2 && (
          <Dropdown className="mx-5 max-w-[12rem] w-full"
            source={statusList}
            value={statusId.toString()}
            getLabel={(x) => x.name}
            getValue={(x) => x.id}
            searchable={true}
            onChange={(value) => setStatus(value as number)}
          />)}
        <SearchForm className="min-w-[12rem] w-full max-w-[25rem]" title={translate("ui.searchspot")} onSubmit={handleSearchSubmit} />
     
        <div className="flex items-center gap-1">

          <div className="w-8 ml-1 cursor-pointer">
            <GridIcon type="grid" active={listMode} size="28px" onClick={() => setListMode(true)} />
          </div>
          <div className="w-8 ml-1 cursor-pointer">
            <GridIcon className="cursor-pointer" type="list" active={!listMode} size="28px" onClick={() => setListMode(false)} />
          </div>
        </div>
      </div>

      {!isLoaded ? (
        (selectedCustomer == "" )?
          (
          <div className="w-full text-center my-10">{translate("ui.selectCustomer")}</div>

          ):(
        <div className="w-full text-center my-10">{translate("common.loading")}</div>
          )
      ) : (
        <div className="flex-1 overflow-auto px-10">
          {!listMode ? (
            <div className="overflow-x-auto my-10 border border-gray-100 rounded-xl shadow-2xl dark:border-zinc-600">
              <table className="table-auto w-full border-collapse">
                <thead className="border-b border-gray-300  text-sm text-gray-600 dark:text-gray-50 dark:border-zinc-900">
                  <tr>
                    {colHeaders.map((header, index) => (
                      <th
                        key={index}
                        className="px-4 py-2 text-left cursor-pointer bg-gray-200 dark:bg-zinc-900"
                        onClick={() => handleSortCategoryChange(header.sort)}
                      >
                        <div className=" flex items-center">
                          {translate("ui." + header.name.toLowerCase())}
                          {sortCategory?.name === header.sort ? (
                            sortCategory.direction ? (
                              <ArrowDownNarrowWide className="w-3 h-3" />
                            ) : (
                              <ArrowUpWideNarrow className="w-3 h-3" />
                            )
                          ) : <div className="w-3" />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{spotItems}</tbody>
              </table>
            </div>
          ) : (
            <div className="content-body flex gap-4 flex-wrap my-10">
              {spotItems}
            </div>
          )}



        </div>)}


      <Popup
        isOpen={editSpotId !== null}
        onClose={() => setEditSpot(null)}
        title={translate("ui.spotinfo")}
         className="w-[80%] max-h-[90vh] overflow-auto"
        
      >
        <SpotInfo onChanged={(spot) => { updateSpot(spot); }}  onClose={() => {setEditSpot(null); }} orderId={null} spotId={editSpotId ?? ""} customerId={selectedCustomer} allowTemplate={customers.find(c => c.id === selectedCustomer)?.allowTemplate} />
      </Popup>


       
    </div>
  );
}
